import { patchOf } from './patch';
import { z } from 'zod';
import { ORDER_CHANNELS } from './checkout';
import { addressSchema } from './core';

export const FISCAL_DOC_TYPES = ['NFE', 'NFCE', 'NFSE'] as const;
export type FiscalDocTypeKey = (typeof FISCAL_DOC_TYPES)[number];
export const FISCAL_DOC_STATUSES = ['QUEUED', 'PROCESSING', 'AUTHORIZED', 'REJECTED', 'CANCELED', 'ERROR'] as const;

export const FISCAL_DOC_LABELS: Record<FiscalDocTypeKey, string> = {
  NFE: 'NF-e (venda com entrega)',
  NFCE: 'NFC-e (venda no balcão)',
  NFSE: 'NFS-e (serviço)',
};

/** Prazo legal de cancelamento por tipo, em horas (a tela mostra o tempo restante). */
export const FISCAL_CANCEL_WINDOW_HOURS: Record<FiscalDocTypeKey, number> = { NFE: 24, NFCE: 24, NFSE: 24 * 30 };

/** Avisos de certificado vencendo (dias antes). */
export const CERTIFICATE_ALERT_DAYS = [30, 15, 7] as const;

const digits = (n: number, label: string) => z.string().trim().regex(new RegExp(`^\\d{${n}}$`), `${label} deve ter ${n} dígitos.`);

/** Quando emitir sozinho (seção 12.3). */
export const autoIssueRulesSchema = z.object({
  enabled: z.boolean().default(false),
  /** Só quando o comprador informou CPF/CNPJ. */
  onlyWithDocument: z.boolean().default(false),
  minCents: z.coerce.number().int().min(0).nullable().default(null),
  /** Canais que emitem; `QUICK_SALE` = venda rápida no balcão. */
  channels: z.array(z.enum([...ORDER_CHANNELS, 'QUICK_SALE'])).default(['STOREFRONT', 'MANUAL', 'WHATSAPP', 'QUICK_SALE']),
});
export type AutoIssueRules = z.infer<typeof autoIssueRulesSchema>;

export const fiscalAddressSchema = addressSchema.omit({ isDefault: true }).extend({
  ibgeCode: z.string().regex(/^\d{7}$/, 'Código IBGE com 7 dígitos.'),
});

export const fiscalSettingsSchema = z
  .object({
    stateRegistration: z.string().trim().max(20).nullable(),
    municipalRegistration: z.string().trim().max(20).nullable(),
    cnae: digits(7, 'O CNAE').nullable(),
    defaultCfop: digits(4, 'O CFOP').nullable(),
    issRatePercent: z.coerce.number().min(0).max(5).nullable(),
    nfeSeries: z.coerce.number().int().min(1).max(999),
    nfceSeries: z.coerce.number().int().min(1).max(999),
    /** CSC da NFC-e (cifrado no banco; nunca volta na API). `null` remove. */
    nfceCsc: z.object({ id: z.string().trim().min(1).max(10), token: z.string().trim().min(16).max(64) }).nullable(),
    address: fiscalAddressSchema,
    autoIssueRules: patchOf(autoIssueRulesSchema),
  })
  .partial();
export type FiscalSettingsInput = z.infer<typeof fiscalSettingsSchema>;

/** Códigos tributários do perfil (nomes neutros; o adapter traduz para o provedor). */
export const taxCodesSchema = z
  .object({
    csosn: digits(3, 'O CSOSN').optional(),
    icmsCst: digits(2, 'O CST do ICMS').optional(),
    icmsRatePercent: z.coerce.number().min(0).max(100).optional(),
    pisCst: digits(2, 'O CST do PIS').optional(),
    cofinsCst: digits(2, 'O CST da COFINS').optional(),
    ibsCbsCst: digits(3, 'O CST do IBS/CBS').optional(),
    ibsCbsClass: digits(6, 'A classificação do IBS/CBS').optional(),
    serviceCode: z.string().trim().max(20).optional(),
  })
  .strict();

export const taxProfileSchema = z.object({
  name: z.string().trim().min(2).max(80),
  docType: z.enum(FISCAL_DOC_TYPES),
  cfopInState: digits(4, 'O CFOP').nullable().optional(),
  cfopOutState: digits(4, 'O CFOP').nullable().optional(),
  taxCodes: taxCodesSchema,
  isDefault: z.boolean().default(false),
});
export const updateTaxProfileSchema = patchOf(taxProfileSchema);

/** Perfis-modelo para o Simples Nacional. Sempre "confirme com seu contador". */
export const TAX_PROFILE_TEMPLATES: (z.input<typeof taxProfileSchema> & { key: string; note: string })[] = [
  {
    key: 'simples-revenda-nfe',
    name: 'Revenda — Simples Nacional (NF-e)',
    docType: 'NFE',
    cfopInState: '5102',
    cfopOutState: '6108',
    taxCodes: { csosn: '102', pisCst: '49', cofinsCst: '49' },
    isDefault: true,
    note: 'Venda de mercadoria comprada de terceiros para consumidor final. Confirme com seu contador.',
  },
  {
    key: 'simples-revenda-nfce',
    name: 'Balcão — Simples Nacional (NFC-e)',
    docType: 'NFCE',
    cfopInState: '5102',
    cfopOutState: null,
    taxCodes: { csosn: '102', pisCst: '49', cofinsCst: '49' },
    isDefault: true,
    note: 'Venda presencial ao consumidor final. Confirme com seu contador.',
  },
  {
    key: 'simples-producao-nfe',
    name: 'Fabricação própria — Simples Nacional (NF-e)',
    docType: 'NFE',
    cfopInState: '5101',
    cfopOutState: '6107',
    taxCodes: { csosn: '101', pisCst: '49', cofinsCst: '49' },
    isDefault: false,
    note: 'Produto fabricado pela empresa. O CSOSN 101 exige alíquota de crédito: confirme com seu contador.',
  },
  {
    key: 'simples-servico-nfse',
    name: 'Serviço — Simples Nacional (NFS-e)',
    docType: 'NFSE',
    cfopInState: null,
    cfopOutState: null,
    taxCodes: { serviceCode: '' },
    isDefault: true,
    note: 'Informe o código de serviço da lista nacional. Confirme com seu contador.',
  },
];

export const issueDocumentSchema = z.object({
  orderId: z.string().uuid(),
  type: z.enum(FISCAL_DOC_TYPES).optional(),
});

export const cancelDocumentSchema = z.object({
  reason: z.string().trim().min(15, 'A justificativa precisa de pelo menos 15 caracteres.').max(255),
});

export const correctionLetterSchema = z.object({
  text: z.string().trim().min(15, 'A correção precisa de pelo menos 15 caracteres.').max(1000),
});

export const listFiscalDocumentsQuerySchema = z.object({
  status: z.enum(FISCAL_DOC_STATUSES).optional(),
  type: z.enum(FISCAL_DOC_TYPES).optional(),
  orderId: z.string().uuid().optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const fiscalExportQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mês no formato AAAA-MM.'),
});

export const certificateUploadSchema = z.object({
  password: z.string().min(1, 'Informe a senha do certificado.').max(200),
});
